/* eslint-disable @typescript-eslint/no-explicit-any */

import { PropsWithChildren, useContext } from 'react';

import { InventoryForm } from '@/components/Inventory/InventoryForm';
import { InventoryFormContext } from '@/contexts/InventoryFormContext';
import { LayoutContext } from '@/contexts/LayoutContext';
import { getUnlockPriceLabel } from '@/tools/inventory';
import { RichText, YoutubeEmbedded } from '@lamesarv-sdk/components';
import { IInventory, InventoryField } from '@lamesarv-sdk/types';

interface OverviewItemProps {
  label: string;
  value?: string | number;
  icon: string;
}

const OverviewItem = ({ icon, label, value }: OverviewItemProps) => (
  <div className="flex flex-col items-center p-4 bg-neutral-100">
    <img className="h-8 mb-3" src={icon} alt={label} />
    <div className="text-xs uppercase text-center">{label}</div>
    <div className="text-xm font-bold text-center">{value || 'N/A'}</div>
  </div>
);

const SectionTitle = ({ children }: PropsWithChildren<unknown>) => (
  <h3 className="font-bold text-md uppercase pb-4 mb-4 border-b border-neutral-200 text-sage-800">{children}</h3>
);

export interface IDetailsContentProps {
  item: IInventory;
  locationsOptionSet?: any;
}

export const DetailsContent = ({ item, locationsOptionSet }: IDetailsContentProps) => {
  const { openMobileInquire } = useContext(InventoryFormContext);
  const { buttonLabel } = getUnlockPriceLabel(item);
  const {
    context: { detailsSettings: settings },
  } = useContext(LayoutContext);

  return (
    <div className="bg-neutral-100 px-4 md:px-6">
      <div className="container px-4 md:px-0 mx-auto py-8" id="pdpform">
        <div className="flex flex-row gap-4 items-start">
          {/* Content */}
          <div className="flex flex-col gap-6 md:basis-2/3">
            <div className="bg-white fixed bottom-0 w-screen p-4 z-20 left-0 shadow-[0px_-5px_50px_10px_#00000026] md:hidden">
              <button
                onClick={() => openMobileInquire(item)}
                type="submit"
                className="w-full cursor-pointer rounded-sm bg-[#1E1E1E] border border-transparent hover:border-black hover:bg-white hover:text-black p-4 text-center text-sm font-bold uppercase text-white text-opacity-90 shadow-sm hover:shadow-md"
              >
                {buttonLabel}
              </button>
            </div>

            {/* Overview */}
            <div id="overview" className="">
              <SectionTitle>Overview</SectionTitle>
              <div className="grid grid-cols-3 gap-px bg-gray-200">
                <OverviewItem icon={settings.bodyStyleIcon} label="RV Class" value={item[InventoryField.body]} />
                <OverviewItem icon={settings.fuelTypeIcon} label="Fuel Type" value={item[InventoryField.fuelType]} />
                {/* <OverviewItem icon={inventoryDetailsSettings.sleepsIcon} label="Sleeps" value="7 Beds" /> */}
                <OverviewItem
                  icon={settings.mileageIcon}
                  label="Mileage"
                  value={item[InventoryField.mileage].toLocaleString()}
                />
                <OverviewItem
                  icon={settings.slideoutsIcon}
                  label="Slideouts"
                  value={item[InventoryField.slideOutsCount]}
                />
                <OverviewItem
                  icon={settings.lengthIcon}
                  label="Length"
                  value={item[InventoryField.length] && `${item[InventoryField.length]} ft.`}
                />
                {/* <OverviewItem icon={inventoryDetailsSettings.weightIcon} label="Weight" value="4,000 lbs." /> */}
                <OverviewItem icon={settings.engineIcon} label="Engine" value={item[InventoryField.engine]} />
                {/*
                <div className="bg-neutral-100 md:hidden" /> */}
              </div>
            </div>

            {/* Video */}
            {!!item[InventoryField.videoUrl] && (
              <div id="video">
                <SectionTitle>Video</SectionTitle>
                <YoutubeEmbedded videoUrl={item[InventoryField.videoUrl]} />
              </div>
            )}

            {/* Description */}
            <div id="description" className="">
              <SectionTitle>Description</SectionTitle>
              <div className="text-sm">
                <RichText content={item[InventoryField.description]} className="mb-0" />
              </div>
            </div>

            {/* Floor-plan */}
            <div id="floorplan" className="">
              <SectionTitle>Floor Plan</SectionTitle>
              <img className="mx-auto border border-neutral-400" src={item[InventoryField.floorPlanImage]} alt="" />
            </div>
            {/* Features */}
            {/* <div id="features" className="">
              <SectionTitle>Features</SectionTitle>
              <div className="text-sm  text-neutral-400">Coming soon...</div>
            </div> */}
            {/* Specifications */}
            {/* <div id="specifications" className="">
              <SectionTitle>Specifications</SectionTitle>
              <div className="text-sm  text-neutral-400">Coming soon...</div>
            </div> */}
          </div>
          {/* Content Form */}
          <div className="bg-white shadow-sm p-6 basis-1/3 hidden md:block" data-testid="form-container">
            <InventoryForm item={item} locationsOptionSet={locationsOptionSet} />
          </div>
        </div>
      </div>
    </div>
  );
};
